import { useContext } from 'react'
import { useParams } from 'react-router'
import { type StoreConfigurationScopedRouteParams } from '../../legacy/common/types'
import { isTanstackShellEnabled } from './tanstack/shellFlag'
import { NativeScopeContext } from './nativeScopeContext'

/**
 * The sole eager reader of scope route params. Every scope hook
 * (`usePartnerId`, `useRetailerId(s)`, `useStoreConfigId(s)`) and the scope-picker
 * gate derive from this, so the RRv5-vs-TanStack read lives in exactly one place.
 *
 * Returns the same shape RRv5's `useParams<StoreConfigurationScopedRouteParams>()`
 * did — a given route only carries the scopes it declares, but the type mirrors the
 * long-standing convention so the derived hooks keep their exact signatures.
 *
 * Impl is bound once at module load, never per render:
 * - Flag-off: the RRv5-only body. Never touches TanStack context, so the boot
 *   bundle stays free of @tanstack (see `shellFlag`).
 * - Flag-on: reads the shell-provided `NativeScopeContext` when a native route owns
 *   the URL, else the RRv5 params bubbling through the legacy `$` catch-all.
 */
// Exported for unit tests; production code uses the module-eval-bound `useScopeParams`.
export const useScopeParamsLegacy = (): StoreConfigurationScopedRouteParams =>
  useParams<StoreConfigurationScopedRouteParams>()

export const useScopeParamsShellAware = (): StoreConfigurationScopedRouteParams => {
  const native = useContext(NativeScopeContext)
  // RRv5 read stays unconditional (rules of hooks). The shell's root layout mounts
  // a legacy <Router> above every route — native and catch-all — so this read
  // always has a router context (empty params on a native route, real ones on the
  // legacy tree). See `tanstackShell` RootLayout.
  const legacy = useParams<StoreConfigurationScopedRouteParams>()

  if (!native.isNative) return legacy

  // On a native route the params come from TanStack (via context); legacy is the
  // per-key fallback so the return keeps its all-string shape.
  return {
    partner_id: native.params.partner_id ?? legacy.partner_id,
    warehouse_id: native.params.warehouse_id ?? legacy.warehouse_id,
    storeConfigId: native.params.storeConfigId ?? legacy.storeConfigId,
  }
}

export const useScopeParams = isTanstackShellEnabled()
  ? useScopeParamsShellAware
  : useScopeParamsLegacy
